/* eslint-disable indent */
import { Search } from "lucide-react";
import Image from "next/image";
import styled from "styled-components";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
    DialogContent as ShadcnDialogContent,
    DialogHeader as ShadcnDialogHeader,
    DialogTitle as ShadcnDialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SelectContent, SelectTrigger } from "@/components/ui/select";
import { Text } from "@/utils/styling/typography.styled";
import { device } from "utils/sizes";
import { colourScheme } from "utils/styling/colours";

export const StyledDialogContent = styled(ShadcnDialogContent)`
    background-color: ${colourScheme.primary};
    color: ${colourScheme.white};
    padding: 1rem;
    width: 100%;
    max-width: 100vw;
    max-height: 90vh;
    overflow: hidden;
    z-index: 1001;

    ${device.md} {
        max-width: 80vw;
    }
    ${device.lg} {
        max-width: 50vw;
    }
`;

export const StyledDialogHeader = styled(ShadcnDialogHeader)`
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: ${colourScheme.primary};
    padding: 1rem;
    margin-bottom: 1rem;
`;

export const HeaderContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

export const StyledDialogTitle = styled(ShadcnDialogTitle)`
    font-size: 1.25rem;
    font-weight: bold;

    ${device.md} {
        font-size: 1.5rem;
    }
`;

export const CloseButton = styled(Button)`
    margin-right: -0.5rem;

    ${device.md} {
        display: none;
    }
`;

export const SearchContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0 1rem;
    margin-bottom: 1rem;

    ${device.md} {
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
        padding: 0;
    }
`;

export const SearchInputContainer = styled.div`
    position: relative;
    flex-grow: 1;
`;

export const SearchIcon = styled(Search)`
    position: absolute;
    left: 0.5rem;
    top: 0.75rem;
    width: 1rem;
    height: 1rem;
    color: ${colourScheme.greyLight};
`;

export const StyledInput = styled(Input)`
    padding-left: 2rem;
    background-color: ${colourScheme.primaryDark};
    border-color: ${colourScheme.primaryLight};
    color: ${colourScheme.white};
    width: 100%;
`;

export const StyledSelectTrigger = styled(SelectTrigger)`
    width: 100%;
    max-width: 180px;
    background-color: ${colourScheme.primaryDark};
    border-color: ${colourScheme.primaryLight};
    color: ${colourScheme.white};

    ${device.md} {
        margin-left: 0.5rem;
    }
`;

export const StyledSelectContent = styled(SelectContent)`
    background-color: ${colourScheme.primaryDark};
    border-color: ${colourScheme.primaryLight};
    color: ${colourScheme.white};
`;

export const StyledScrollArea = styled(ScrollArea)`
    height: calc(90vh - 180px);
    padding: 0 1rem;

    ${device.md} {
        padding: 0;
        padding-right: 1rem;
    }
`;

export const ModelsGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center; /* Center the items horizontally */
    gap: 1rem; /* Add gap between items */
`;

export const StyledCard = styled(Card)`
    background-color: ${colourScheme.primaryDark};
    border-color: ${colourScheme.primaryLight};
    cursor: pointer;
    transition: background-color 0.2s ease-in-out;

    &:hover {
        background-color: ${colourScheme.primaryLight};
    }
`;

export const StyledCardContent = styled.div`
    padding: 0.75rem;
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
`;

export const ModelImage = styled(Image)`
    border-radius: 0.375rem;
`;

export const ModelInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

export const ModelHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 0.25rem;
`;

export const ModelTitle = styled(Text)`
    font-size: 1rem;
    font-weight: 600;
    white-space: wrap;
    color: ${colourScheme.white};
`;

export const BadgeContainer = styled.div`
    display: flex;
    flex-shrink: 0;
    margin-left: 0.25rem;
    flex-direction: column;
    gap: 0.25rem;
`;

export const NewBadge = styled(Badge)`
    background: linear-gradient(
        90deg,
        ${colourScheme.successLight} 0%,
        ${colourScheme.successDark} 100%
    );
    color: ${colourScheme.primary};
    padding: 0.25rem 0.5rem;
    border-radius: 1rem;
    font-size: 0.75rem;
    font-weight: bold;
    margin-top: 0.5rem;

    ${device.md} {
        margin-left: 0.5rem;
        margin-top: 0.5rem;
    }

    ${device.xl} {
        white-space: nowrap;
    }
`;

export const SubBadge = styled(Badge)`
    background-color: ${colourScheme.alertLight};
    color: ${colourScheme.white};
    font-size: 0.75rem;
    margin-left: 0.25rem;
`;

export const CategoryBadge = styled(Badge)`
    margin-top: 0.25rem;
    background-color: ${colourScheme.greyDark};
    color: ${colourScheme.greyLight};
    font-size: 0.75rem;
`;

export const ModelDescriptionText = styled.p`
    font-size: 0.875rem;
    color: ${colourScheme.greyLight};
    white-space: wrap;
`;

export const DefaultModelContainer = styled.button`
    display: flex;
    align-items: center;
    padding: 0.5rem;
    gap: 1rem;
    border: 1px solid ${colourScheme.primaryLight};
    border-radius: 0.5rem;
    cursor: pointer;
    background-color: ${colourScheme.primary};
    color: ${colourScheme.white};
    width: 100%;

    &:hover {
        background-color: ${colourScheme.primaryLight};
    }

    img {
        border-radius: 0.5rem;
    }
`;

export const LabelWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;

export const ModelName = styled.div`
    font-weight: bold;
`;

export const ModelDescription = styled.div`
    font-size: 0.875rem;
    color: ${colourScheme.greyLight};
`;

export const ArtStyleCard = styled.div`
    border-radius: 0.5rem;
    padding: 1rem;
    cursor: pointer;
    text-align: center;
    flex: 1 1 200px; /* Allow the card to grow and shrink */
    max-width: 300px; /* Set a maximum width */
    margin: 0.5rem; /* Add margin for spacing */
    background-color: ${colourScheme.primaryDark};
    border: 1px solid ${colourScheme.primaryLight};
    transition: background-color 0.2s ease-in-out;

    &:hover {
        background-color: ${colourScheme.primaryLight};
    }
`;

export const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;

    max-width: 100vw;
    width: 100%;
    padding: 1rem;
    max-height: 90vh;
    min-height: 50vh;
    overflow-y: auto;

    ${device.lg} {
        max-width: 70vw;
    }

    ${device.xl} {
        max-width: 60vw;
    }
`;

export const ArtStyleName = styled.div`
    margin-top: 0.5rem;
    font-weight: bold;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    ${device.xl} {
        flex-direction: row;
        justify-content: space-between;
    }
`;
